import type { ContractResponse } from '@domain/schemas/contract.schema';
import type { AuditEntry } from '@root/@types/audit-log';
import type { ResourceBlock, ResourceSection } from '@root/@types/base';
import type {
  AuditLogInfo,
  DataExtractionData,
  DynamoDBEndorsement,
  DynamoDBResourceComment,
  DynamoDBResourceSection,
  DynamoDBRootResource,
  RootResourceStatus,
  User,
} from '@root/@types/types';

type ResourceBlockOmitKeys = 'commentsCount' | 'visibilityComputed' | 'validationErrors';

export interface LibraryBlockType extends Omit<ResourceBlock, ResourceBlockOmitKeys> {
  selected?: boolean;
  variationsCount: number;
}

export type LibraryBlockTypeForListPage = Omit<LibraryBlockType, 'last_updated_by' | 'created_by'> & {
  created_by?: string;
  last_updated_by?: string;
  usedInTemplatesCount?: number;
  parentBlockId?: string;
  subRows?: LibraryBlockTypeForListPage[];
};

// https://docs.aws.amazon.com/guardduty/latest/ug/monitoring-malware-protection-s3-scans-gdu.html
export type MalwareScanStatus = 'NO_THREATS_FOUND' | 'THREATS_FOUND' | 'UNSUPPORTED' | 'ACCESS_DENIED' | 'FAILED';

export interface Attachment {
  id: string;
  name: string;
  fileName: string;
  size: number;
  path: string;
  type?: 'pre' | 'post' | string;
  status?: 'pending' | 'ready' | string;
  malware_scan_status?: MalwareScanStatus | 'PENDING' | string;
  attachment_id?: string;
  block_id?: string;
  block_type?: 'block' | 'summary_block';
}

export interface AttachmentFE {
  id: string;
  fileName: string;
  name: string;
  size: number;
  status?: 'pending' | 'ready' | string;
  malware_scan_status?: MalwareScanStatus | string;
}

export type AuditLogItem = {
  id: string;
  attachment_id?: string;
  created_at: string;
  block_id: string | null;
  block_name: string | null;
  changeBaseTemplateMetadata?: {
    nextSourceResourceDisplayName: string;
    prevSourceResourceDisplayName: string;
  };
  entity_id?: string;
  entity_name?: string;
  bulk_block_deletes?: { block_id: string | null; block_name: string | null }[];
  updated_data_items?: string[] | null;
  updated_status?: { from: RootResourceStatus; to: RootResourceStatus };
  user?: User;
} & AuditLogInfo;

export type RevisionHistoryItem = {
  revision_id: string;
  revision_name: string;
  revision_created_at: string;
  updated_at: string;
  last_updated_by: User;
};

export interface Section extends DynamoDBResourceSection {
  blocks: ResourceBlock[];
}

export type EntityData = Omit<DynamoDBRootResource, 'data_items' | 'status'> & {
  attachments?: Attachment[];
  audit_log: AuditEntry[];
  comments: DynamoDBResourceComment[];
  dataExtractions?: DataExtractionData[];
  data_items: Record<string, Record<string, string | number> | any>;
  parentId?: string;
  revision_history?: RevisionHistoryItem[];
  sections: Section[];
  status: RootResourceStatus;
  teams?: number[];
  contract_validation_checks?: boolean;
  contract_number_of_required_data_points?: number;
  contract_missing_data?: { id: string; title: string }[];
  hasMaterialChanges?: boolean;
};

export interface EndorsementData
  extends Omit<EntityData, 'parentId' | 'status' | 'contract_validation_checks' | 'contract_missing_data'>,
    DynamoDBEndorsement {
  endorsement_summary_template_id?: number;
  summary: {
    blocksExcludedFromPreview: string[];
    layout: string[];
    sections: ResourceSection[];
  };
}

export const isEndorsementData = (entity?: Pick<ContractResponse, 'pk' | 'sk'>): entity is EndorsementData => {
  if (!entity) {
    return false;
  }

  return entity?.sk?.startsWith('endorsements#');
};

export type SubmissionPatchResponse =
  | {
      message: 'Server Error';
    }
  | { message: 'success'; blockId?: string };

export type PublishingOptionType = {
  id: RootResourceStatus;
  title: string;
  description: string;
};

export type UsedInTemplates = {
  id: string;
  name: string;
}[];
